import type { Enrollment, Progress, Qualification, TimelineStatus } from '../types';
import { getDefaultDeadlineDays } from '../data/skillTrees';
import { isItemCompleted } from './skillProgress';

/** Add days to a date, returning a new Date */
export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/** Days between two dates (negative if b < a) */
export const daysBetween = (a: Date, b: Date): number => {
  const msPerDay = 86400000;
  return Math.round((b.getTime() - a.getTime()) / msPerDay);
};

/**
 * Get effective deadline days-from-start for a qualification in an enrollment.
 * Returns override if set, else default, else null (no deadline).
 */
export const getEffectiveDeadlineDays = (
  enrollment: Enrollment,
  qualId: string,
): number | null => {
  if (qualId in enrollment.deadlineOverrides) {
    return enrollment.deadlineOverrides[qualId];
  }
  return getDefaultDeadlineDays(enrollment.skillTreeId, qualId);
};

/**
 * Get the absolute deadline date for a qualification.
 * Returns null if no deadline is configured.
 */
export const getDeadlineDate = (
  enrollment: Enrollment,
  qualId: string,
): Date | null => {
  const days = getEffectiveDeadlineDays(enrollment, qualId);
  if (days == null) return null;
  return addDays(enrollment.startDate, days);
};

/** Days remaining until deadline. Negative = overdue. */
export const getDaysRemaining = (deadlineDate: Date): number => {
  return daysBetween(new Date(), deadlineDate);
};

/**
 * Compute signoffs-per-day pace needed to complete a qualification by its deadline.
 * Returns null if no deadline or already complete.
 */
export const getRequiredPace = (
  remainingSignoffs: number,
  deadlineDate: Date | null,
): number | null => {
  if (!deadlineDate || remainingSignoffs <= 0) return null;
  const daysLeft = getDaysRemaining(deadlineDate);
  if (daysLeft <= 0) return remainingSignoffs; // need all remaining today
  return remainingSignoffs / daysLeft;
};

/**
 * Get the completion status for a qualification relative to its deadline.
 */
export const getTimelineStatus = (
  deadlineDate: Date | null,
  totalItems: number,
  completedItems: number,
): TimelineStatus => {
  if (totalItems > 0 && completedItems >= totalItems) return 'complete';
  if (!deadlineDate) return 'no-deadline';

  const daysLeft = getDaysRemaining(deadlineDate);
  if (daysLeft < 0) return 'overdue';

  // Calculate pace needed vs reasonable pace
  const remaining = totalItems - completedItems;
  const pace = remaining / Math.max(daysLeft, 1);

  // Heuristics: > 3 signoffs/day is behind, > 1.5 is at-risk
  if (pace > 3) return 'behind';
  if (pace > 1.5) return 'at-risk';
  return 'on-track';
};

/**
 * For a qualification, find the earliest and latest signoff dates
 * to determine time-at-level and when the student was working on it.
 */
export const getQualificationTiming = (
  qualId: string,
  progress: Record<string, Progress>,
): { firstSignoff: Date | null; lastSignoff: Date | null; daysSpent: number | null } => {
  let earliest: Date | null = null;
  let latest: Date | null = null;

  for (const [key, p] of Object.entries(progress)) {
    if (!key.startsWith(qualId + '_') || !p.completed || !p.completedAt) continue;
    const d = p.completedAt instanceof Date ? p.completedAt : new Date(p.completedAt);
    if (!earliest || d < earliest) earliest = d;
    if (!latest || d > latest) latest = d;
  }

  return {
    firstSignoff: earliest,
    lastSignoff: latest,
    daysSpent: earliest && latest ? daysBetween(earliest, latest) + 1 : null,
  };
};

/**
 * Build a full timeline summary for a student's enrollment.
 * Returns one entry per qualification in the tree.
 */
export const buildTimeline = (
  enrollment: Enrollment,
  qualifications: Qualification[],
  qualificationIds: string[],
  progress: Record<string, Progress>,
): TimelineEntry[] => {
  return qualificationIds.map((qualId) => {
    const qual = qualifications.find((q) => q.id === qualId);
    if (!qual) {
      return {
        qualId,
        qualName: qualId,
        qualShortName: qualId,
        totalItems: 0,
        completedItems: 0,
        deadlineDate: null,
        deadlineDays: null,
        daysRemaining: null,
        requiredPace: null,
        status: 'no-deadline' as TimelineStatus,
        timing: { firstSignoff: null, lastSignoff: null, daysSpent: null },
      };
    }

    const totalItems = qual.items.length;
    let completedItems = 0;
    for (const item of qual.items) {
      if (isItemCompleted(progress, qualId, item.id)) completedItems++;
    }

    const deadlineDays = getEffectiveDeadlineDays(enrollment, qualId);
    const deadlineDate = getDeadlineDate(enrollment, qualId);
    const daysRemaining = deadlineDate ? getDaysRemaining(deadlineDate) : null;
    const remainingSignoffs = totalItems - completedItems;
    const requiredPace = getRequiredPace(remainingSignoffs, deadlineDate);
    const status = getTimelineStatus(deadlineDate, totalItems, completedItems);
    const timing = getQualificationTiming(qualId, progress);

    return {
      qualId,
      qualName: qual.name,
      qualShortName: qual.shortName,
      totalItems,
      completedItems,
      deadlineDate,
      deadlineDays,
      daysRemaining,
      requiredPace,
      status,
      timing,
    };
  });
};

export interface TimelineEntry {
  qualId: string;
  qualName: string;
  qualShortName: string;
  totalItems: number;
  completedItems: number;
  deadlineDate: Date | null;
  deadlineDays: number | null; // days from start
  daysRemaining: number | null;
  requiredPace: number | null; // signoffs/day to hit deadline
  status: TimelineStatus;
  timing: {
    firstSignoff: Date | null;
    lastSignoff: Date | null;
    daysSpent: number | null;
  };
}
